import { useEffect, useRef, type ReactNode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter, MemoryRouter, useLocation } from "react-router-dom";
import "../globals.css";
import "../index.css";
import PageComponent from "./ScaffoldingInFleetApp";

type AnyProps = Record<string, any>;

const DEFAULT_URL = "/ScaffoldingInFleetPage";

let root:
    | ReturnType<typeof createRoot>
    | ReturnType<typeof hydrateRoot>
    | null = null;

/**
 * The memory router doesn't touch the address bar, so a page that changes only its
 * query or hash (a filter calling setSearchParams) would leave the URL behind and a
 * reload would lose it. Copies them to the address bar; moves to other routes (a web
 * app's sub-pages) are left alone, the URL has no path for them.
 *
 * Wraps the page rather than sitting next to it: a sibling would shift the useId()
 * values of the page against the SSR render, which has no such component.
 */
function AddressBarSync({ children }: { children: ReactNode }) {
    const location = useLocation();
    const start = useRef(location);

    useEffect(() => {
        if (location.key === start.current.key || location.pathname !== start.current.pathname) return;
        const url = window.location.pathname + location.search + location.hash;
        if (url !== window.location.pathname + window.location.search + window.location.hash) {
            window.history.replaceState(window.history.state, "", url);
        }
    }, [location]);

    return children;
}

function App(props: AnyProps) {
    const { router = "memory", basename, initialEntries, ...componentProps } = props;

    if (router === "memory") {
        return (
            <MemoryRouter initialEntries={initialEntries ?? [DEFAULT_URL]}>
                <AddressBarSync>
                    <PageComponent {...(componentProps as any)} />
                </AddressBarSync>
            </MemoryRouter>
        );
    }

    return (
        <BrowserRouter basename={basename}>
            <PageComponent {...(componentProps as any)} />
        </BrowserRouter>
    );
}

function mount(el: HTMLElement, props: AnyProps = {}) {
    root = createRoot(el);
    root.render(<App {...props} />);
}

function hydrate(el: HTMLElement, props: AnyProps = {}) {
    root = hydrateRoot(el, <App {...props} />);
}

function unmount() {
    root?.unmount?.();
    root = null;
}

if (typeof window !== "undefined")
    (window as any).ScaffoldingInFleetVC = { mount, hydrate, unmount };